'use client';

// ============================================================================
// Page « adresse introuvable », PEINTE AUX COULEURS DE L'ADRESSE D'ARRIVEE.
// ----------------------------------------------------------------------------
// Le dessin est celui d'avant, au pixel pres : halos qui derivent, trame, gros
// 404 en degrade, deux boutons, note de bas de page. Une seule chose change :
// AUCUNE COULEUR N'EST PLUS ECRITE ICI. Tout vient des variables du systeme
// (--acc, --acc2, --acc-rgb...), posees par l'appelant a partir de l'identite
// de l'hote. Le bouton principal suit automatiquement : `components/ui/button`
// lit deja var(--acc) et var(--acc-on).
//
// Le contenu du texte, lui, dit OU l'on se trouve. Un salarie perdu sur le
// portail lisait « Vyndra Cloud » : il ne savait meme pas s'il etait au bon
// endroit.
// ============================================================================

import { motion } from 'framer-motion';
import Link from 'next/link';
import type { CSSProperties } from 'react';
import { ArrowLeft, House as Home } from '@phosphor-icons/react';
import { VyndraLogo } from '@/components/vyndra-logo';
import { Button } from '@/components/ui/button';
import { FondEcosysteme } from '@/components/fonds/fond-ecosysteme';

export interface PageIntrouvableProps {
  /** Variables CSS de la palette de l'hote (voir components/espace/couleurs). */
  variables: CSSProperties;
  /** Nom de l'endroit : « Vyndra », « Espace salarié », « Guard Informatique ». */
  nom: string;
  /** Adresse de retour, garantie d'exister sur CET hote. */
  accueil: string;
  libelleAccueil: string;
  /** Vrai hors du back-office : le prisme Vyndra n'y est alors pas la marque. */
  marqueTierce: boolean;
  /** Couleur de l'hote pour teinter le fond commun (univers, portail), sinon celles du V. */
  accentFond?: string | null;
}

export function PageIntrouvable({
  variables,
  nom,
  accueil,
  libelleAccueil,
  marqueTierce,
  accentFond = null,
}: PageIntrouvableProps) {
  return (
    <div
      className="relative min-h-screen w-full overflow-hidden flex items-center justify-center p-6"
      style={variables}
    >
      {/* Le fond commun, celui de la connexion (29/09/2026), teinte de l'univers
          de l'hote. Il remplace deux halos flous animes sans fin et une grille. */}
      <FondEcosysteme accent={accentFond} />

      <div className="relative z-10 flex flex-col items-center text-center max-w-[520px]">
        <motion.div
          initial={{ opacity: 0, scale: 0.5, rotate: -180 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-6"
        >
          {marqueTierce ? (
            // Hors du back-office, le prisme violet et cyan n'est pas la marque
            // des lieux : on ecrit LE NOM DE L'HOTE, a sa couleur.
            //
            // CE N'EST PLUS UNE INITIALE (21/09/2026). Ce sceau portait la
            // premiere lettre du nom, et « ? » quand le nom manquait. Le
            // dirigeant refuse ces lettres inventees : « il y a un vrai logo
            // dans les reglages en PNG, ce n'est pas pour que tu m'inventes je
            // ne sais quoi ». Cette page est servie SANS lecture d'identite
            // (rien n'est telecharge, donc rien ne peut manquer) : elle n'a pas
            // les fichiers de logo, et le repli juste est donc le nom entier.
            <span
              className="block max-w-[420px] rounded-[22px] px-5 py-3 text-center font-display text-[26px] font-bold leading-tight"
              style={{
                background: 'var(--acc-degrade)',
                color: 'var(--acc-on)',
                boxShadow: '0 14px 34px rgba(var(--acc-rgb),0.32)',
              }}
            >
              {nom.trim() || 'Espace introuvable'}
            </span>
          ) : (
            <VyndraLogo size={72} />
          )}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="font-display text-[120px] font-bold leading-none tracking-[-4px] mb-2"
          style={{
            background: 'var(--acc-degrade)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          404
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.5 }}
        >
          <h2 className="font-display text-[22px] font-bold text-[#E8E9EB] mb-3 tracking-[-0.3px]">
            Page introuvable
          </h2>
          <p className="text-[13px] text-[#8A8F98] leading-relaxed max-w-[420px] mb-2">
            On a cherché partout, rien à cette adresse.
          </p>
          <p className="text-[12px] text-[#5A5F6A] max-w-[420px]">
            Le lien est peut-être obsolète, ou la page n&apos;a jamais existé. Vous
            êtes bien sur <span className="font-semibold text-[#C4C7CC]">{nom}</span>,
            tout le reste est encore là.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="flex flex-wrap justify-center gap-3 mt-8"
        >
          <Button
            variant="secondary"
            size="lg"
            onClick={() => window.history.back()}
            className="group min-h-[44px]"
          >
            <ArrowLeft
              size={14}
              className="transition-transform group-hover:-translate-x-0.5"
            />
            Retour
          </Button>
          <Link href={accueil}>
            <Button variant="primary" size="lg" className="group min-h-[44px]">
              <Home size={14} />
              {libelleAccueil}
              <span className="ml-1 transition-transform group-hover:translate-x-0.5">→</span>
            </Button>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-12 text-[10px] text-[#3A3D45] font-mono tracking-wide"
        >
          [ERR_PAGE_NOT_FOUND] · Pas de panique, même les meilleurs se perdent parfois.
        </motion.div>
      </div>
    </div>
  );
}
